/*
Handles core OIDC functionality
*/
import {
	stringConstants,
	statusConstants,
	Platform,
	getOIDCRedirectURI,
} from '../AuthConstants';
import { OIDCConfiguration, StoreConfiguration } from '../../Common/TypeDefinitions';
import { Logger } from '../Logger';
import { logConstants } from '../../utils/Constants';
import {
	OIDCEndpointsTableKey,
	OIDCTokensTableKey,
} from './TypeDefinitions';
import { config } from '../AuthConfig';
import DSAuth from '../dsAuth/DSAuth';
import { GenericRequest, ProtectionMode } from '../dsAuth/data/AuthTypes';
import { StringMap } from '@openid/appauth/built/types';
import { AuthorizationRequest } from '@openid/appauth/built/authorization_request';
import {
	AuthorizationServiceConfiguration,
	AuthorizationServiceConfigurationJson,
} from '@openid/appauth/built/authorization_service_configuration';
import { OIDCAuthorizationRequestHandler } from './OIDCAuthorizationRequestHandler';
import { PerformAuthorizationRequestResponse } from './TypeDefinitions';
import StoreConfigManager from '../StoreConfigManager';
import OIDCEndpointsManager from './OIDCEndpointsManager';
import OIDCTokenManager from './OIDCTokenManager';
import { RevokeTokenRequest } from '@openid/appauth/built/revoke_token_request';
import ctxsNative from '../ctxsNative';
import { GRANT_TYPE_REFRESH_TOKEN } from '@openid/appauth/built/token_request';
import OIDCTokenRequestHandler from './OIDCTokenRequestHandler';
import AuthMan from '../AuthMan';
import { SentryLogger } from '../../sentryLogging/SentryLogger';
import * as Sentry from '@sentry/browser';
import Utils from '../../utils/Utils';
import { CryptoParams } from '../main';

export default class OIDC {
	private static instance: OIDC;
	static getInstance(): OIDC {
		if (!OIDC.instance) {
			OIDC.instance = new OIDC();
		}
		return OIDC.instance;
	}

	async fetchOIDCEndPoints(
		requestParams: GenericRequest,
		referenceUri: string,
		id: string
	): Promise<AuthorizationServiceConfigurationJson> {
		const { method, url, headers, body } = requestParams;
		const response = await DSAuth.getInstance().fetch(
			method,
			url,
			headers,
			body,
			referenceUri
		);
		if (response.statusCode === statusConstants['200']) {
			const endpoints = JSON.parse(response.body);
			/* eslint-disable @typescript-eslint/naming-convention */
			return {
				authorization_endpoint: endpoints.authorization_endpoint,
				token_endpoint: endpoints.token_endpoint,
				revocation_endpoint: endpoints.revocation_endpoint,
			};
			/* eslint-enable @typescript-eslint/naming-convention */
		} else {
			Logger.getInstance().write(
				logConstants.ERROR,
				id +
					':' +
					stringConstants['ERROR_OIDC_ENDPOINTS_FETCH_FAILURE'] +
					'returned due to response status:' +
					response.statusCode
			);
			throw new Error(stringConstants['ERROR_OIDC_ENDPOINTS_FETCH_FAILURE']);
		}
	}

	async fetchAndStoreOIDCEndPoints(
		referenceUri: string,
		storeConfig: StoreConfiguration,
		id: string,
		transactionId: string
	): Promise<AuthorizationServiceConfigurationJson> {
		const configuration = storeConfig[referenceUri];
		if (configuration.oidcConfiguration) {
			let isOIDCSupported = false;

			isOIDCSupported = this.isOIDCClientSupported(
				configuration.oidcConfiguration,
				config.platform
			);
			if (isOIDCSupported) {
				const headers = DSAuth.getInstance().addBasicHeaders({}, transactionId);
				const params = {
					method: stringConstants['GET'],
					url: configuration.oidcConfiguration.oidc_discovery_endpoint,
					headers,
					body: '',
				};
				const key = {
					referenceUri: referenceUri,
					serviceURL: configuration.tokenServiceURL,
					authDomain: configuration.authDomain,
				};
				let fetchedEndPoints: AuthorizationServiceConfigurationJson;
				try {
					fetchedEndPoints = await this.fetchOIDCEndPoints(params, key.referenceUri, id);
					const endPoints = {
						authorizationEndpoint: fetchedEndPoints.authorization_endpoint,
						tokenEndpoint: fetchedEndPoints.token_endpoint,
						revocationEndpoint: fetchedEndPoints.revocation_endpoint,
					};
					Logger.getInstance().write(
						logConstants.INFO,
						JSON.stringify({ endPoints, key })
					);
					await OIDCEndpointsManager.getInstance().saveOIDCEndpoints(key, endPoints);
					return fetchedEndPoints;
				} catch (e) {
					const cachedEndpoints =
						await OIDCEndpointsManager.getInstance().retrieveOIDCEndpoints(key);
					if (cachedEndpoints) {
						/* eslint-disable @typescript-eslint/naming-convention */
						fetchedEndPoints = {
							authorization_endpoint: cachedEndpoints.authorizationEndpoint,
							revocation_endpoint: cachedEndpoints.revocationEndpoint,
							token_endpoint: cachedEndpoints.tokenEndpoint,
						};
						/* eslint-enable @typescript-eslint/naming-convention */
						return fetchedEndPoints;
					} else {
						throw new Error(stringConstants['ERROR_OIDC_ENDPOINTS_UNAVAILABLE']);
					}
				}
			} else {
				Logger.getInstance().write(
					logConstants.ERROR,
					id + ':' + stringConstants['ERROR_OIDC_CLIENT_NOT_SUPPORTED']
				);
				throw new Error(stringConstants['ERROR_OIDC_CLIENT_NOT_SUPPORTED']);
			}
		} else {
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ':' + stringConstants['ERROR_MISSING_OIDC_CONFIGURATION']
			);
			throw new Error(stringConstants['ERROR_MISSING_OIDC_CONFIGURATION']);
		}
	}

	/**
	 * Revokes all refresh tokens in the database when user logs off
	 * @param id request ID
	 */
	async revokeAllRefreshTokens(id: string, protectMode?: ProtectionMode): Promise<void> {
		Logger.getInstance().write(logConstants.INFO, id + ': Revoking all refresh tokens');

		// Fetch all OIDC table keys (authdomain and referenceUri)
		const storeConfigs = AuthMan.getInstance().getStoreConfiguration();
		// Fetch all OIDC endpoint table entries
		const listOfOIDCEndpointTableEntries =
			await OIDCEndpointsManager.getInstance().retrieveAllOIDCEndPointTableEntries();
		// Fetch the entries in the OIDCTokensTable
		const listOfTokenTableEntries =
			await OIDCTokenManager.getInstance().retrieveAllTokenTableEntries();

		// Iterate through the list of referenceUri, authDomain pairs and get the corresponding revocation endpoint and refresh token from above two lists
		// Make revocation request to each of them

		const referenceUriList = storeConfigs ? Object.keys(storeConfigs) : [];
		for (const referenceUri of referenceUriList) {
			const authDomain = storeConfigs[referenceUri].authDomain;
			try {
				let revocationEndPoint;
				const revocationEndPointEntry = listOfOIDCEndpointTableEntries.filter((item) => {
					return item.authDomain === authDomain && item.referenceUri === referenceUri;
				});
				if (revocationEndPointEntry.length > 0) {
					revocationEndPoint = revocationEndPointEntry[0].revocationEndpoint;
					const athenaClientId = StoreConfigManager.getInstance().getClientid(
						storeConfigs[referenceUri].oidcConfiguration,
						config.platform
					);

					const oidcTokenTableEntry = listOfTokenTableEntries.filter((item) => {
						return item.authDomain === authDomain && item.referenceUri === referenceUri;
					});

					if (oidcTokenTableEntry.length > 0) {
						let decryptedToken;
						if (Utils.getInstance().isBridgeFunctionSupported(ctxsNative, 'DecryptV2')) {
							const params: CryptoParams = {
								referenceUri: referenceUri,
								token: oidcTokenTableEntry[0].refreshToken,
								protectionMode: protectMode,
							};
							decryptedToken = await ctxsNative.DecryptV2(JSON.stringify(params));
						} else {
							decryptedToken = await ctxsNative.Decrypt(
								referenceUri,
								oidcTokenTableEntry[0].refreshToken
							);
						}
						const revocationRequest = new RevokeTokenRequest({
							token: decryptedToken,
							// eslint-disable-next-line @typescript-eslint/naming-convention
							client_id: athenaClientId,
							// eslint-disable-next-line @typescript-eslint/naming-convention
							token_type_hint: GRANT_TYPE_REFRESH_TOKEN,
						});

						Logger.getInstance().write(
							logConstants.DEBUG,
							id +
								': Making revocation request revocation endpoint, endpoint : ' +
								revocationEndPoint +
								'referenceUri' +
								referenceUri
						);

						await OIDCTokenRequestHandler.getInstance().performRevokeTokenRequest(
							revocationEndPoint,
							revocationRequest,
							referenceUri,
							id,
							id
						);
					}
				}
			} catch (e) {
				SentryLogger.getInstance().logError(e, { tags: { url: referenceUri } });
				Logger.getInstance().write(
					logConstants.ERROR,
					id + ': Error revoking refresh token for ' + referenceUri + e
				);
			}
		}
	}

	/**
	 * Revokes the refresh token by retrieving revocation endpoint and refresh token from corresponding tables using the table keys received as input
	 *
	 * @param {OIDCEndpointsTableKey} endpointsTableKey
	 * @param {OIDCTokensTableKey} tokenTableKey
	 * @param {OIDCConfiguration} oidcConfiguration
	 * @return {*}  {Promise<void>}
	 * @memberof OIDC
	 */
	async revokeRefreshToken(
		endpointsTableKey: OIDCEndpointsTableKey,
		tokenTableKey: OIDCTokensTableKey,
		oidcConfiguration: OIDCConfiguration,
		transactionId: string
	): Promise<void> {
		try {
			const revocationEndPointEntry =
				await OIDCEndpointsManager.getInstance().retrieveOIDCEndpoints(endpointsTableKey);
			const revocationEndPoint = revocationEndPointEntry?.revocationEndpoint;
			if (!revocationEndPoint) {
				return;
			}
			const oidcTokenTableEntry = await OIDCTokenManager.getInstance().retrieveOIDCTokens(
				tokenTableKey
			);
			const token = oidcTokenTableEntry?.refreshToken;
			if (!token) {
				return;
			}

			const athenaClientId = StoreConfigManager.getInstance().getClientid(
				oidcConfiguration,
				config.platform
			);
			const revocationRequest = new RevokeTokenRequest({
				token,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				client_id: athenaClientId,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				token_type_hint: GRANT_TYPE_REFRESH_TOKEN,
			});

			Logger.getInstance().write(
				logConstants.DEBUG,
				': Making revocation request revocation endpoint, endpoint : ' +
					revocationEndPoint +
					'referenceUri' +
					oidcTokenTableEntry.referenceUri
			);

			await OIDCTokenRequestHandler.getInstance().performRevokeTokenRequest(
				revocationEndPoint,
				revocationRequest,
				oidcTokenTableEntry.referenceUri,
				transactionId
			);
		} catch (e) {
			SentryLogger.getInstance().logError(e, {
				tags: { url: endpointsTableKey.referenceUri },
			});
			Logger.getInstance().write(
				logConstants.ERROR,
				': Error revoking refresh token for ' + endpointsTableKey.referenceUri + ':' + e
			);
		}
	}

	isOIDCClientSupported(oidcConfig: OIDCConfiguration, platform: Platform): boolean {
		try {
			//TODO-LLT: move this inside OIDC PrimaryTokenService
			const clientId = StoreConfigManager.getInstance().getClientid(oidcConfig, platform);
			if (clientId) {
				return true;
			}
			return false;
		} catch (e) {
			const breadcrumb = {
				category: 'OIDCClient',
				message: e.message,
				level: Sentry.Severity.Error,
			};
			SentryLogger.getInstance().addBreadCrumb(breadcrumb);
			Logger.getInstance().write(logConstants.ERROR, e.message);
			return false;
		}
	}

	async getAuthorizationWebviewURLAndRequest(
		referenceUri: string,
		endpoints: AuthorizationServiceConfigurationJson,
		clientId: string,
		id: string
	): Promise<PerformAuthorizationRequestResponse> {
		Logger.getInstance().write(
			logConstants.INFO,
			id + ': Preparing authorization webview URL and Request'
		);
		const { acrValues, authDomain, promptLoginEnabled } =
			AuthMan.getInstance().getStoreConfiguration()[referenceUri];
		/* eslint-disable @typescript-eslint/naming-convention */
		const acr_values = !!acrValues
			? `${acrValues} nr:1`
			: 'tenant:' + authDomain + ' product:fd nr:1';
		/* eslint-disable @typescript-eslint/naming-convention */
		const extras: StringMap = {
			access_type: 'offline',
			acr_values,
		};
		if (promptLoginEnabled === true) {
			extras['prompt'] = 'login';
		}
		const scope = config.compressedGroupsEnabled
			? stringConstants.OIDC_SCOPES_WITH_COMPRESSED_GROUPS
			: stringConstants.OIDC_SCOPES;
		const request = new AuthorizationRequest({
			client_id: clientId,
			redirect_uri: getOIDCRedirectURI(config.platform, config.hostApp),
			scope,
			response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
			state: undefined,
			extras: extras,
		});
		/* eslint-enable @typescript-eslint/naming-convention */

		//this returns the web view start url and authorization request
		return await OIDCAuthorizationRequestHandler.getInstance().performAuthorizationRequest(
			new AuthorizationServiceConfiguration(endpoints),
			request
		);
	}
}
